import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth, API, authFetch } from "@/App";
import { toast } from "sonner";
import { EditProfileForm } from "@/components/community/EditProfileForm";
import { HubZone } from "@/components/community/HubZone";
import { DiscussionBoardZone } from "@/components/community/DiscussionBoardZone";
import { MessagesZone } from "@/components/community/MessagesZone";
import { NetworkingLoungeZone } from "@/components/community/NetworkingLoungeZone";
import { MarketplaceZone } from "@/components/community/MarketplaceZone";
import { MastermindPodsZone } from "@/components/community/MastermindPodsZone";
import { ResourceVaultZone } from "@/components/community/ResourceVaultZone";
import { NewsEventsZone } from "@/components/community/NewsEventsZone";
import PodcastSuiteZone from "@/components/community/PodcastSuiteZone";
import { CommunityDialogs } from "@/components/community/CommunityDialogs";
import {
  Users, Briefcase, Gift, Target, Handshake,
  Plus, MessageSquare, Mail, Megaphone, Mic,
  ArrowLeft, Loader2, Crown, Camera, Bell, ChevronRight, XCircle
} from "lucide-react";

// Helper function to strip HTML tags from text
const stripHtml = (html) => {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
};

export default function CommunityPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeZone, setActiveZone] = useState("hub");
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [pods, setPods] = useState([]);
  const [resources, setResources] = useState([]);
  const [resourceCategories, setResourceCategories] = useState([]);
  const [suggestedConnections, setSuggestedConnections] = useState([]);
  const [leaderboard, setLeaderboard] = useState(null);
  const [leaderboardPeriod, setLeaderboardPeriod] = useState("month");
  const [membershipStatus, setMembershipStatus] = useState(null);
  const [newsFeed, setNewsFeed] = useState([]);
  const [events, setEvents] = useState([]);
  const [discussionPosts, setDiscussionPosts] = useState([]);
  const [discussionCategory, setDiscussionCategory] = useState("all");
  const [membersDirectory, setMembersDirectory] = useState([]);
  
  // Messaging states
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  
  // Filter states
  const [opportunityFilter, setOpportunityFilter] = useState("all");
  const [resourceCategory, setResourceCategory] = useState("all");
  const [expandedOppId, setExpandedOppId] = useState(null);
  const [expandedResourceId, setExpandedResourceId] = useState(null);
  const [expandedFeedId, setExpandedFeedId] = useState(null);
  
  // Dialog states
  const [showPostOpportunity, setShowPostOpportunity] = useState(false);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [showIntroductionDialog, setShowIntroductionDialog] = useState(false);
  const [showCollaborationDialog, setShowCollaborationDialog] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [showCreateBulletin, setShowCreateBulletin] = useState(false);
  const [showCreateEvent, setShowCreateEvent] = useState(false);
  const [introductionText, setIntroductionText] = useState("");
  const [collaborationGoal, setCollaborationGoal] = useState("");
  const [newOpportunity, setNewOpportunity] = useState({
    title: "", opportunity_type: "partnership", description: "",
    looking_for: "", ideal_partner: "", timeline: "", contact_method: ""
  });
  const [newBulletin, setNewBulletin] = useState({ title: "", content: "", category: "announcement" });
  const [newEvent, setNewEvent] = useState({ title: "", description: "", date: "", time: "", duration: "60", meeting_link: "" });
  const [newDiscussionPost, setNewDiscussionPost] = useState({ content: "", category: "general" });
  const [showPostDiscussion, setShowPostDiscussion] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [showComments, setShowComments] = useState(false);
  const [postComments, setPostComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  
  // Resource management states
  const [showAddResource, setShowAddResource] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [newResource, setNewResource] = useState({
    title: "", description: "", category: "templates",
    resource_url: "", resource_type: "link", is_featured: false
  });

  // Pod management states
  const [selectedPod, setSelectedPod] = useState(null);
  const [showPodDetails, setShowPodDetails] = useState(false);
  const [showAddPodContent, setShowAddPodContent] = useState(false);
  const [showCreatePod, setShowCreatePod] = useState(false);
  const [showEditPod, setShowEditPod] = useState(false);
  const [podMembers, setPodMembers] = useState([]);
  const [newPodContent, setNewPodContent] = useState({
    content_type: "announcement", title: "", description: "", url: ""
  });
  const [newPod, setNewPod] = useState({ name: "", description: "", pod_type: "custom" });
  const [workshops, setWorkshops] = useState([]);
  const [podcasts, setPodcasts] = useState([]);

  // Recommendation request states
  const [showRequestRecommendation, setShowRequestRecommendation] = useState(false);
  const [myRecommendations, setMyRecommendations] = useState([]);
  const [newRecommendation, setNewRecommendation] = useState({
    category: "", budget: "", current_tools: "", specific_needs: "", urgency: "normal"
  });

  // Photo upload & activity dashboard states
  const [showPhotoUpload, setShowPhotoUpload] = useState(false);
  const [activityDashboard, setActivityDashboard] = useState(null);
  const [profileCardUserId, setProfileCardUserId] = useState(null);
  const [profileCardData, setProfileCardData] = useState(null);
  const [profileCardLoading, setProfileCardLoading] = useState(false);
  const [showMemberManagement, setShowMemberManagement] = useState(false);
  const [managedMembers, setManagedMembers] = useState(null);
  const [memberFilter, setMemberFilter] = useState("all");
  const [agentNotifications, setAgentNotifications] = useState([]);
  const [agentUnreadCount, setAgentUnreadCount] = useState(0);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [referralData, setReferralData] = useState(null);

  // ─── Data Fetching ──────────────────────────────────────────

  useEffect(() => { fetchInitialData(); }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [onboardingRes, podsRes, categoriesRes, leaderboardRes, membershipRes, newsFeedRes, eventsRes] = await Promise.all([
        authFetch(`${API}/community/onboarding-status`),
        authFetch(`${API}/community/pods`),
        authFetch(`${API}/community/resources/categories`),
        authFetch(`${API}/community/leaderboard?period=month`),
        authFetch(`${API}/community/membership-status`),
        authFetch(`${API}/community/news-feed`),
        authFetch(`${API}/community/events`)
      ]);
      if (onboardingRes.ok) setOnboardingStatus(await onboardingRes.json());
      if (podsRes.ok) { const d = await podsRes.json(); setPods(d.pods || []); }
      if (categoriesRes.ok) { const d = await categoriesRes.json(); setResourceCategories(d.categories || []); }
      if (leaderboardRes.ok) setLeaderboard(await leaderboardRes.json());
      if (membershipRes.ok) setMembershipStatus(await membershipRes.json());
      if (newsFeedRes.ok) { const d = await newsFeedRes.json(); setNewsFeed(d.feed || []); }
      if (eventsRes.ok) { const d = await eventsRes.json(); setEvents(d.events || []); }

      if (user?.role === "consultant") {
        try { const r = await authFetch(`${API}/community/admin/activity-dashboard`); if (r.ok) setActivityDashboard(await r.json()); } catch (_e) { /* ignore */ }
      }
      try { const r = await authFetch(`${API}/community/notifications`); if (r.ok) { const d = await r.json(); setAgentNotifications(d.notifications || []); setAgentUnreadCount(d.unread_count || 0); } } catch (_e) { /* ignore */ }
      try { const r = await authFetch(`${API}/community/referral-code`); if (r.ok) setReferralData(await r.json()); } catch (_e) { /* ignore */ }
    } catch (error) { console.error("Error fetching data:", error); }
    finally { setLoading(false); }
  };

  const fetchLeaderboard = async (period = "month") => {
    try { const res = await authFetch(`${API}/community/leaderboard?period=${period}`); if (res.ok) { setLeaderboard(await res.json()); setLeaderboardPeriod(period); } }
    catch (error) { console.error("Error fetching leaderboard:", error); }
  };

  const canAccessZone = (zone) => {
    if (!membershipStatus) return true;
    const premiumZones = ["messages"];
    return premiumZones.includes(zone) ? membershipStatus.has_premium_access : true;
  };

  const handleZoneClick = (zone) => {
    if (!canAccessZone(zone)) { setShowUpgradeDialog(true); return; }
    handleZoneChange(zone);
  };

  const handleZoneChange = (zone) => {
    setActiveZone(zone);
    if (zone === "marketplace") fetchOpportunities();
    else if (zone === "vault") fetchResources();
    else if (zone === "hub") fetchSuggestedConnections();
    else if (zone === "board") { fetchDiscussionPosts(discussionCategory); fetchMembersDirectory(); }
    else if (zone === "messages") { fetchConversations(); fetchMembersDirectory(); }
  };

  // ─── Upgrade ────────────────────────────────────────────────

  const handleUpgrade = async () => {
    try {
      const res = await authFetch(`${API}/community/subscribe`, { method: "POST" });
      if (res.ok) { const data = await res.json(); window.location.href = data.checkout_url; }
      else toast.error("Failed to start subscription");
    } catch (_e) { toast.error("Error starting subscription"); }
  };

  // ─── Bulletin / Event Handlers ──────────────────────────────

  const handleCreateBulletin = async () => {
    if (!newBulletin.title || !newBulletin.content) { toast.error("Please fill in all fields"); return; }
    try {
      const res = await authFetch(`${API}/community/bulletin/create`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(newBulletin) });
      if (res.ok) { toast.success("Bulletin posted!"); setShowCreateBulletin(false); setNewBulletin({ title: "", content: "", category: "announcement" }); fetchInitialData(); }
      else toast.error("Failed to post bulletin");
    } catch (_e) { toast.error("Error posting bulletin"); }
  };

  const handleCreateEvent = async () => {
    if (!newEvent.title || !newEvent.date || !newEvent.time) { toast.error("Please fill in all required fields"); return; }
    try {
      const res = await authFetch(`${API}/community/events/create`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(newEvent) });
      if (res.ok) { toast.success("Event created!"); setShowCreateEvent(false); setNewEvent({ title: "", description: "", date: "", time: "", duration: "60", meeting_link: "" }); fetchInitialData(); }
      else toast.error("Failed to create event");
    } catch (_e) { toast.error("Error creating event"); }
  };

  const handleRegisterEvent = async (eventId) => {
    try {
      const res = await authFetch(`${API}/community/events/${eventId}/register`, { method: "POST" });
      if (res.ok) { toast.success("Registered for event!"); fetchInitialData(); }
      else { const d = await res.json(); toast.error(d.detail || "Failed to register"); }
    } catch (_e) { toast.error("Error registering"); }
  };

  // ─── Discussion Handlers ────────────────────────────────────

  const fetchDiscussionPosts = async (category = "all") => {
    try {
      let url = `${API}/community/discussion`;
      if (category && category !== "all") url += `?category=${category}`;
      const res = await authFetch(url);
      if (res.ok) { const d = await res.json(); setDiscussionPosts(d.posts || []); }
    } catch (error) { console.error("Error fetching discussion posts:", error); }
  };

  const fetchMembersDirectory = async () => {
    try {
      const res = await authFetch(`${API}/community/members/directory?limit=50`);
      if (res.ok) { const d = await res.json(); setMembersDirectory(d.members || []); }
    } catch (error) { console.error("Error fetching members:", error); }
  };

  const handleCreateDiscussionPost = async () => {
    if (!newDiscussionPost.content.trim()) { toast.error("Please enter some content"); return; }
    try {
      const res = await authFetch(`${API}/community/discussion`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(newDiscussionPost) });
      if (res.ok) { toast.success("Post shared!"); setShowPostDiscussion(false); setNewDiscussionPost({ content: "", category: "general" }); fetchDiscussionPosts(discussionCategory); }
      else toast.error("Failed to post");
    } catch (_e) { toast.error("Error posting"); }
  };

  const handleLikePost = async (postId) => {
    try { const res = await authFetch(`${API}/community/discussion/${postId}/like`, { method: "POST" }); if (res.ok) fetchDiscussionPosts(discussionCategory); }
    catch (error) { console.error("Error liking post:", error); }
  };

  const handleViewComments = async (post) => {
    setSelectedPost(post);
    try { const res = await authFetch(`${API}/community/discussion/${post.post_id}/comments`); if (res.ok) { const d = await res.json(); setPostComments(d.comments || []); setShowComments(true); } }
    catch (error) { console.error("Error fetching comments:", error); }
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedPost) return;
    try {
      const res = await authFetch(`${API}/community/discussion/${selectedPost.post_id}/comments`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ content: newComment }) });
      if (res.ok) { setNewComment(""); handleViewComments(selectedPost); fetchDiscussionPosts(discussionCategory); }
    } catch (_e) { toast.error("Error adding comment"); }
  };

  // ─── Messaging Handlers ─────────────────────────────────────

  const fetchConversations = async () => {
    try { const res = await authFetch(`${API}/community/messages/conversations`); if (res.ok) { const d = await res.json(); setConversations(d.conversations || []); setUnreadCount(d.total_unread || 0); } }
    catch (error) { console.error("Error fetching conversations:", error); }
  };

  const fetchMessages = async (userId) => {
    try { const res = await authFetch(`${API}/community/messages/${userId}`); if (res.ok) { const d = await res.json(); setConversationMessages(d.messages || []); setActiveConversation(d.other_user); fetchConversations(); } }
    catch (error) { console.error("Error fetching messages:", error); }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !activeConversation) return;
    try {
      const res = await authFetch(`${API}/community/messages/${activeConversation.user_id}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ content: newMessage }) });
      if (res.ok) { setNewMessage(""); fetchMessages(activeConversation.user_id); }
      else { const d = await res.json(); toast.error(d.detail || "Failed to send message"); }
    } catch (_e) { toast.error("Error sending message"); }
  };

  const handleStartConversation = async (member) => {
    setSelectedRecipient(member);
    setActiveConversation({ user_id: member.user_id, name: member.name, picture: member.picture, business_description: member.business_description });
    await fetchMessages(member.user_id);
    setShowNewMessage(false);
  };

  // ─── Opportunity Handlers ───────────────────────────────────

  const fetchOpportunities = async (type = null) => {
    try {
      let url = `${API}/community/opportunities?status=open`;
      if (type && type !== "all") url += `&opportunity_type=${type}`;
      const res = await authFetch(url);
      if (res.ok) { const d = await res.json(); setOpportunities(d.opportunities || []); }
    } catch (error) { console.error("Error fetching opportunities:", error); }
  };

  const handlePostOpportunity = async () => {
    try {
      const res = await authFetch(`${API}/community/opportunities`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(newOpportunity) });
      if (res.ok) {
        toast.success("Opportunity posted!"); setShowPostOpportunity(false);
        setNewOpportunity({ title: "", opportunity_type: "partnership", description: "", looking_for: "", ideal_partner: "", timeline: "", contact_method: "" });
        fetchOpportunities();
      } else toast.error("Failed to post opportunity");
    } catch (_e) { toast.error("Error posting opportunity"); }
  };

  // ─── Resource Handlers ──────────────────────────────────────

  const fetchResources = async (category = null) => {
    try {
      let url = `${API}/community/resources`;
      if (category && category !== "all") url += `?category=${category}`;
      const res = await authFetch(url);
      if (res.ok) { const d = await res.json(); setResources(d.resources || []); }
    } catch (error) { console.error("Error fetching resources:", error); }
  };

  const fetchResourceCategories = async () => {
    try {
      const res = await authFetch(`${API}/community/resources/categories`);
      if (res.ok) { const d = await res.json(); setResourceCategories(d.categories || []); }
    } catch (e) { console.error(e); }
  };


  const handleAddResource = async () => {
    if (!newResource.title.trim() || !newResource.category) { toast.error("Please fill in title and category"); return; }
    try {
      const res = await authFetch(`${API}/community/resources`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(newResource) });
      if (res.ok) { toast.success("Resource added to vault!"); setShowAddResource(false); setNewResource({ title: "", description: "", category: "templates", resource_url: "", resource_type: "link", is_featured: false }); fetchResources(resourceCategory); }
      else toast.error("Failed to add resource");
    } catch (_e) { toast.error("Error adding resource"); }
  };

  const handleEditResource = (resource) => {
    setEditingResource(resource);
    setNewResource({ title: resource.title || "", description: resource.description || "", category: resource.category || "templates", resource_url: resource.resource_url || "", resource_type: resource.resource_type || "link", is_featured: resource.is_featured || false });
    setShowAddResource(true);
  };

  const handleUpdateResource = async () => {
    if (!editingResource) return;
    try {
      const res = await authFetch(`${API}/community/resources/${editingResource.resource_id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(newResource) });
      if (res.ok) { toast.success("Resource updated!"); setShowAddResource(false); setEditingResource(null); setNewResource({ title: "", description: "", category: "templates", resource_url: "", resource_type: "link", is_featured: false }); fetchResources(resourceCategory); }
      else toast.error("Failed to update resource");
    } catch (_e) { toast.error("Error updating resource"); }
  };

  const handleDeleteResource = async (resourceId) => {
    if (!window.confirm("Are you sure you want to delete this resource?")) return;
    try { const res = await authFetch(`${API}/community/resources/${resourceId}`, { method: "DELETE" }); if (res.ok) { toast.success("Resource deleted"); fetchResources(resourceCategory); } else toast.error("Failed to delete resource"); }
    catch (_e) { toast.error("Error deleting resource"); }
  };

  // ─── Connection Handlers ────────────────────────────────────

  const fetchSuggestedConnections = async () => {
    try { const res = await authFetch(`${API}/community/suggested-connections`); if (res.ok) { const d = await res.json(); setSuggestedConnections(d.suggestions || []); } }
    catch (error) { console.error("Error fetching suggestions:", error); }
  };

  const handleConnect = async (targetUserId, targetUserName) => {
    try {
      const res = await authFetch(`${API}/community/connect/${targetUserId}`, { method: "POST" });
      if (res.ok) {
        const data = await res.json();
        if (data.already_connected) toast.info(`You're already connected with ${targetUserName || 'this member'}`);
        else { toast.success(`Connected with ${targetUserName || 'member'}!`); fetchSuggestedConnections(); if (data.conversation_id) toast.info("Start a conversation in the Messages tab!", { duration: 4000 }); }
      } else { const error = await res.json(); toast.error(error.detail || "Failed to connect"); }
    } catch (_e) { toast.error("Error connecting with member"); }
  };

  // ─── Onboarding / Profile Handlers ──────────────────────────

  const handleOnboardingTask = (taskKey) => {
    switch (taskKey) {
      case "profile_completed": setShowEditProfile(true); break;
      case "photo_uploaded": setShowPhotoUpload(true); break;
      case "introduction_posted": setShowIntroductionDialog(true); break;
      case "first_connection_made": fetchSuggestedConnections(); toast.info("Check out the suggested connections below!"); break;
      case "pod_joined": setActiveZone("pods"); break;
      case "collaboration_goal_set": setShowCollaborationDialog(true); break;
      default: break;
    }
  };

  const handlePostIntroduction = async () => {
    if (!introductionText.trim()) { toast.error("Please enter your introduction"); return; }
    try {
      const res = await authFetch(`${API}/community/profile`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ introduction_posted: true, introduction: introductionText }) });
      if (res.ok) { toast.success("Introduction posted!"); setShowIntroductionDialog(false); setIntroductionText(""); fetchInitialData(); }
      else toast.error("Failed to post introduction");
    } catch (_e) { toast.error("Error posting introduction"); }
  };

  const handleSetCollaborationGoal = async () => {
    if (!collaborationGoal.trim()) { toast.error("Please enter your collaboration goal"); return; }
    try {
      const res = await authFetch(`${API}/community/profile`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ collaboration_interests: [collaborationGoal] }) });
      if (res.ok) { toast.success("Collaboration goal set!"); setShowCollaborationDialog(false); setCollaborationGoal(""); fetchInitialData(); }
      else toast.error("Failed to set goal");
    } catch (_e) { toast.error("Error setting goal"); }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error("Photo must be under 2MB"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const res = await authFetch(`${API}/community/profile-photo`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ photo_base64: reader.result, content_type: file.type }) });
        if (res.ok) { toast.success("Profile photo uploaded! +5 points"); setShowPhotoUpload(false); fetchInitialData(); }
        else { const err = await res.json(); toast.error(err.detail || "Failed to upload photo"); }
      } catch (_e) { toast.error("Error uploading photo"); }
    };
    reader.readAsDataURL(file);
  };

  const openProfileCard = async (userId) => {
    if (!userId || userId === user?.user_id) return;
    setProfileCardUserId(userId); setProfileCardLoading(true);
    try { const res = await authFetch(`${API}/community/profile/${userId}`); if (res.ok) setProfileCardData(await res.json()); }
    catch (_e) { /* ignore */ }
    setProfileCardLoading(false);
  };

  // ─── Admin Handlers ─────────────────────────────────────────

  const fetchManagedMembers = async () => {
    try { const res = await authFetch(`${API}/consultant/community-members`); if (res.ok) setManagedMembers(await res.json()); }
    catch (_e) { /* ignore */ }
  };

  const handleToggleTier = async (userId, currentTier, memberName) => {
    const newTier = currentTier === "premium" ? "basic" : "premium";
    try {
      const res = await authFetch(`${API}/consultant/community-members/${userId}/upgrade`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tier: newTier }) });
      if (res.ok) { const d = await res.json(); toast.success(d.message); fetchManagedMembers(); }
      else toast.error("Failed to update tier");
    } catch (_e) { toast.error("Error updating tier"); }
  };

  const handleSendProfileReminders = async () => {
    if (!window.confirm("Send profile update reminder emails to all clients and active leads?")) return;
    try {
      toast.loading("Sending profile update reminders...", { id: "profile-reminders" });
      const res = await authFetch(`${API}/community/admin/send-profile-update-reminder`, { method: "POST" });
      if (res.ok) { const d = await res.json(); toast.success(`Sent ${d.emails_sent} reminder emails (${d.clients_count} clients, ${d.leads_count} leads)`, { id: "profile-reminders", duration: 5000 }); }
      else { const err = await res.json(); toast.error(err.detail || "Failed to send reminders", { id: "profile-reminders" }); }
    } catch (_e) { toast.error("Error sending reminders", { id: "profile-reminders" }); }
  };

  // ─── Pod Handlers ───────────────────────────────────────────

  const openPodDetails = async (pod) => {
    setSelectedPod(pod); setShowPodDetails(true);
    try {
      const [detailsRes, membersRes] = await Promise.all([
        authFetch(`${API}/community/pods/${pod.pod_id}/details`),
        authFetch(`${API}/community/pods/${pod.pod_id}/members`)
      ]);
      if (detailsRes.ok) setSelectedPod(await detailsRes.json());
      if (membersRes.ok) { const d = await membersRes.json(); setPodMembers(d.members || []); }
    } catch (error) { console.error("Error fetching pod details:", error); }
  };

  const handleCreatePod = async () => {
    if (!newPod.name.trim() || !newPod.description.trim()) { toast.error("Name and description are required"); return; }
    try {
      const res = await authFetch(`${API}/community/pods`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(newPod) });
      if (res.ok) { toast.success("Pod created successfully!"); setShowCreatePod(false); setNewPod({ name: "", description: "", pod_type: "custom" }); const r = await authFetch(`${API}/community/pods`); if (r.ok) { const d = await r.json(); setPods(d.pods || []); } }
      else { const err = await res.json(); toast.error(err.detail || "Failed to create pod"); }
    } catch (_e) { toast.error("Error creating pod"); }
  };

  const handleUpdatePod = async () => {
    if (!selectedPod) return;
    try {
      const res = await authFetch(`${API}/community/pods/${selectedPod.pod_id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name: selectedPod.name, description: selectedPod.description, pod_type: selectedPod.pod_type }) });
      if (res.ok) { toast.success("Pod updated!"); setShowEditPod(false); const r = await authFetch(`${API}/community/pods`); if (r.ok) { const d = await r.json(); setPods(d.pods || []); } }
      else { const err = await res.json(); toast.error(err.detail || "Failed to update pod"); }
    } catch (_e) { toast.error("Error updating pod"); }
  };

  const handleDeletePod = async (podId) => {
    if (!window.confirm("Are you sure you want to delete this pod? All members will be removed.")) return;
    try {
      const res = await authFetch(`${API}/community/pods/${podId}`, { method: "DELETE" });
      if (res.ok) { toast.success("Pod deleted"); setShowPodDetails(false); setSelectedPod(null); const r = await authFetch(`${API}/community/pods`); if (r.ok) { const d = await r.json(); setPods(d.pods || []); } }
      else { const err = await res.json(); toast.error(err.detail || "Failed to delete pod"); }
    } catch (_e) { toast.error("Error deleting pod"); }
  };

  const handleJoinPod = async (podId) => {
    try {
      const res = await authFetch(`${API}/community/pods/${podId}/join`, { method: "POST" });
      if (res.ok) { toast.success("Joined pod!"); const r = await authFetch(`${API}/community/pods`); if (r.ok) { const d = await r.json(); setPods(d.pods || []); } }
      else { const err = await res.json(); toast.error(err.detail || "Failed to join pod"); }
    } catch (_e) { toast.error("Error joining pod"); }
  };

  const handleLeavePod = async (podId) => {
    try {
      const res = await authFetch(`${API}/community/pods/${podId}/leave`, { method: "POST" });
      if (res.ok) { toast.success("Left pod"); const r = await authFetch(`${API}/community/pods`); if (r.ok) { const d = await r.json(); setPods(d.pods || []); } }
    } catch (_e) { toast.error("Error leaving pod"); }
  };

  const fetchWorkshopsAndPodcasts = async () => {
    try {
      const [workshopsRes, podcastsRes] = await Promise.all([authFetch(`${API}/workshops`), authFetch(`${API}/podcasts`)]);
      if (workshopsRes.ok) { const d = await workshopsRes.json(); setWorkshops(d.workshops || d || []); }
      if (podcastsRes.ok) { const d = await podcastsRes.json(); setPodcasts(d.podcasts || d || []); }
    } catch (error) { console.error("Error fetching workshops/podcasts:", error); }
  };

  const handleAddPodContent = async () => {
    if (!selectedPod || !newPodContent.title.trim()) { toast.error("Title is required"); return; }
    try {
      const res = await authFetch(`${API}/community/pods/${selectedPod.pod_id}/content`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(newPodContent) });
      if (res.ok) { toast.success("Content added to pod!"); setShowAddPodContent(false); setNewPodContent({ content_type: "announcement", title: "", description: "", url: "" }); openPodDetails(selectedPod); }
      else { const err = await res.json(); toast.error(err.detail || "Failed to add content"); }
    } catch (_e) { toast.error("Error adding content"); }
  };

  const handleRemovePodContent = async (contentId) => {
    if (!selectedPod) return;
    try { const res = await authFetch(`${API}/community/pods/${selectedPod.pod_id}/content/${contentId}`, { method: "DELETE" }); if (res.ok) { toast.success("Content removed"); openPodDetails(selectedPod); } }
    catch (_e) { toast.error("Error removing content"); }
  };

  // ─── Recommendation Handlers ────────────────────────────────

  const fetchMyRecommendations = async () => {
    try { const res = await authFetch(`${API}/community/recommendations/my-requests`); if (res.ok) { const d = await res.json(); setMyRecommendations(d.requests || []); } }
    catch (error) { console.error("Error fetching recommendations:", error); }
  };

  const handleSubmitRecommendation = async () => {
    if (!newRecommendation.category || !newRecommendation.budget || !newRecommendation.specific_needs) { toast.error("Please fill in category, budget, and specific needs"); return; }
    try {
      const res = await authFetch(`${API}/community/recommendations/request`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(newRecommendation) });
      if (res.ok) { toast.success("Your recommendation request has been submitted!"); setShowRequestRecommendation(false); setNewRecommendation({ category: "", budget: "", current_tools: "", specific_needs: "", urgency: "normal" }); fetchMyRecommendations(); }
      else { const err = await res.json(); toast.error(err.detail || "Failed to submit request"); }
    } catch (_e) { toast.error("Error submitting request"); }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try { return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric" }); }
    catch { return dateStr; }
  };

  // ─── Render ─────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-950 flex flex-col items-center justify-center gap-4">
        <img src="https://customer-assets.emergentagent.com/job_1f6fc830-db93-46be-8373-0fbe94240b47/artifacts/1h0hcmcd_MBC%20logo%20%28400%20x%20400%20px%29.png" alt="My Biz Consulting" className="w-24 h-24 object-contain animate-pulse" />
        <Loader2 className="w-6 h-6 animate-spin text-[#39FF14]" />
        <p className="text-neutral-400 text-sm">Loading Community...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-neutral-950">
      {/* Hero Header */}
      <div className="bg-gradient-to-r from-emerald-900/30 to-neutral-900 border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 md:gap-6">
              <img src="https://customer-assets.emergentagent.com/job_1f6fc830-db93-46be-8373-0fbe94240b47/artifacts/1h0hcmcd_MBC%20logo%20%28400%20x%20400%20px%29.png" alt="My Biz Consulting" className="w-16 h-16 md:w-20 md:h-20 object-contain" />
              <div>
                <Button variant="ghost" className="text-neutral-400 hover:text-white mb-2 -ml-2" onClick={() => navigate(-1)} data-testid="community-back-btn">
                  <ArrowLeft className="w-4 h-4 mr-2" />Back to Dashboard
                </Button>
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-[#39FF14] uppercase tracking-wider">Business Solutions Center</span>
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-white mb-1">My Biz Consulting Community</h1>
                <p className="text-neutral-400 text-sm md:text-base">Strategic Growth & Partnership Ecosystem for Serious Business Owners</p>
              </div>
            </div>
            <div className="hidden md:flex items-center gap-4">
              <Button onClick={() => setShowPostOpportunity(true)} className="bg-[#39FF14] text-black hover:bg-[#32e612] font-semibold" data-testid="post-opportunity-btn">
                <Plus className="w-4 h-4 mr-2" />Post Opportunity
              </Button>
            </div>
          </div>
          <div className="md:hidden mt-4">
            <Button onClick={() => setShowPostOpportunity(true)} className="bg-[#39FF14] text-black hover:bg-[#32e612] font-semibold w-full" data-testid="post-opportunity-btn-mobile">
              <Plus className="w-4 h-4 mr-2" />Post Opportunity
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Zone Navigation */}
        <div className="flex flex-wrap gap-2 mb-8 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0 md:overflow-visible scrollbar-hide">
          {[
            { id: "hub", label: "Member Hub", icon: Users, premium: false },
            { id: "board", label: "Discussion Board", icon: MessageSquare, premium: false },
            { id: "messages", label: "Messages", icon: Mail, premium: true, showBadge: unreadCount > 0 },
            { id: "lounge", label: "Networking Lounge", icon: Handshake, premium: false },
            { id: "marketplace", label: "Opportunity Marketplace", icon: Briefcase, premium: false },
            { id: "pods", label: "Mastermind Pods", icon: Target, premium: false },
            { id: "vault", label: "Resource Vault", icon: Gift, premium: false },
            { id: "podcast", label: "Podcast Suite", icon: Mic, premium: false },
            { id: "news", label: "News & Events", icon: Megaphone, premium: false }
          ].map((zone) => {
            const Icon = zone.icon;
            const isActive = activeZone === zone.id;
            const isLocked = zone.premium && !canAccessZone(zone.id);
            return (
              <Button
                key={zone.id}
                variant={isActive ? "default" : "outline"}
                onClick={() => handleZoneClick(zone.id)}
                className={`relative whitespace-nowrap ${isActive
                  ? "bg-[#39FF14] text-black hover:bg-[#32e612]"
                  : isLocked
                    ? "border-neutral-700 text-neutral-500 hover:bg-neutral-800"
                    : "border-neutral-700 text-neutral-300 hover:bg-neutral-800 hover:text-white"
                }`}
                data-testid={`zone-${zone.id}`}
              >
                <Icon className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">{zone.label}</span>
                <span className="sm:hidden">{zone.label.split(' ')[0]}</span>
                {zone.showBadge && unreadCount > 0 && (
                  <span className="ml-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
                {isLocked && (
                  <span className="ml-2 text-xs bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded">Premium</span>
                )}
              </Button>
            );
          })}
        </div>

        {/* Agent Notification Banner */}
        {agentUnreadCount > 0 && !showNotifPanel && (
          <div className="mb-4 p-3 bg-gradient-to-r from-[#39FF14]/10 to-neutral-900 rounded-lg border border-[#39FF14]/20 cursor-pointer" onClick={() => setShowNotifPanel(true)} data-testid="agent-nudge-banner">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Bell className="w-5 h-5 text-[#39FF14]" />
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{agentUnreadCount}</span>
                </div>
                <span className="text-sm text-white">{agentNotifications.find(n => !n.read)?.title || "You have new notifications"}</span>
              </div>
              <ChevronRight className="w-4 h-4 text-neutral-400" />
            </div>
          </div>
        )}

        {/* Notification Panel */}
        {showNotifPanel && (
          <div className="mb-4 bg-neutral-800 rounded-lg border border-neutral-700 overflow-hidden" data-testid="notification-panel">
            <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-700">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                <Bell className="w-4 h-4 text-[#39FF14]" /> Notifications
                {agentUnreadCount > 0 && <Badge className="bg-red-500 text-white text-xs">{agentUnreadCount}</Badge>}
              </h3>
              <div className="flex gap-2">
                {agentUnreadCount > 0 && (
                  <Button size="sm" variant="ghost" className="text-xs text-neutral-400" onClick={async () => {
                    await authFetch(`${API}/community/notifications/read-all`, { method: "PUT" });
                    setAgentNotifications(prev => prev.map(n => ({...n, read: true})));
                    setAgentUnreadCount(0);
                  }}>Mark all read</Button>
                )}
                <Button size="sm" variant="ghost" className="text-neutral-400" onClick={() => setShowNotifPanel(false)}>
                  <XCircle className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="max-h-64 overflow-y-auto divide-y divide-neutral-700">
              {agentNotifications.length > 0 ? agentNotifications.slice(0, 10).map((notif) => (
                <div key={notif.notification_id} className={`px-4 py-3 hover:bg-neutral-700/50 transition-colors cursor-pointer ${!notif.read ? "bg-[#39FF14]/5" : ""}`}
                  onClick={async () => {
                    if (!notif.read) {
                      await authFetch(`${API}/community/notifications/${notif.notification_id}/read`, { method: "PUT" });
                      setAgentNotifications(prev => prev.map(n => n.notification_id === notif.notification_id ? {...n, read: true} : n));
                      setAgentUnreadCount(prev => Math.max(0, prev - 1));
                    }
                  }}
                >
                  <div className="flex items-start gap-3">
                    {notif.agent_picture ? (
                      <img src={notif.agent_picture} alt={notif.agent_name || notif.agent} className="w-8 h-8 rounded-full flex-shrink-0 mt-0.5 border border-neutral-600" />
                    ) : (
                      <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${!notif.read ? "bg-[#39FF14]" : "bg-neutral-600"}`} />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white">{notif.title}</p>
                      <p className="text-xs text-neutral-400 mt-0.5">{notif.message}</p>
                      <p className="text-[10px] text-neutral-600 mt-1">{notif.agent_name || notif.agent} {notif.agent_role ? `• ${notif.agent_role}` : ""} &middot; {notif.created_at ? new Date(notif.created_at).toLocaleDateString() : ""}</p>
                    </div>
                  </div>
                </div>
              )) : (
                <div className="px-4 py-8 text-center text-neutral-500 text-sm">No notifications yet</div>
              )}
            </div>
          </div>
        )}

        {/* Membership Status Banner */}
        {membershipStatus && !membershipStatus.has_premium_access && (
          <div className="mb-6 p-4 bg-gradient-to-r from-amber-900/30 to-neutral-900 rounded-lg border border-amber-500/20">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Crown className="w-6 h-6 text-amber-400 flex-shrink-0" />
                <div>
                  <p className="font-medium text-white">Upgrade to Premium</p>
                  <p className="text-sm text-neutral-400">Unlock all zones for just ${membershipStatus.upgrade_price}/month</p>
                  {!membershipStatus.has_profile_photo && (
                    <p className="text-xs text-amber-400 mt-1 flex items-center gap-1">
                      <Camera className="w-3 h-3" /> Upload a profile photo first to unlock upgrade
                    </p>
                  )}
                </div>
              </div>
              <Button
                onClick={() => {
                  if (!membershipStatus.can_upgrade) { setShowPhotoUpload(true); toast.info("Please upload a profile photo before upgrading"); }
                  else setShowUpgradeDialog(true);
                }}
                className={`font-semibold w-full sm:w-auto ${membershipStatus.can_upgrade ? "bg-amber-500 text-black hover:bg-amber-400" : "bg-neutral-700 text-neutral-300 hover:bg-neutral-600"}`}
                data-testid="upgrade-premium-btn"
              >
                {membershipStatus.can_upgrade ? "Upgrade Now" : "Upload Photo to Unlock"}
              </Button>
            </div>
          </div>
        )}

        {/* Zone Content */}
        <div className="space-y-6">
          {activeZone === "hub" && (
            <HubZone
              user={user} onboardingStatus={onboardingStatus} leaderboard={leaderboard}
              leaderboardPeriod={leaderboardPeriod} suggestedConnections={suggestedConnections}
              referralData={referralData} activityDashboard={activityDashboard}
              showMemberManagement={showMemberManagement} setShowMemberManagement={setShowMemberManagement}
              managedMembers={managedMembers} memberFilter={memberFilter} setMemberFilter={setMemberFilter}
              fetchLeaderboard={fetchLeaderboard} handleOnboardingTask={handleOnboardingTask}
              handleConnect={handleConnect} openProfileCard={openProfileCard}
              handleSendProfileReminders={handleSendProfileReminders} fetchManagedMembers={fetchManagedMembers}
              handleToggleTier={handleToggleTier} API={API} authFetch={authFetch}
            />
          )}

          {activeZone === "board" && (
            <DiscussionBoardZone
              discussionPosts={discussionPosts} discussionCategory={discussionCategory}
              setDiscussionCategory={setDiscussionCategory} fetchDiscussionPosts={fetchDiscussionPosts}
              membersDirectory={membersDirectory} handleLikePost={handleLikePost}
              handleViewComments={handleViewComments} openProfileCard={openProfileCard}
              setShowPostDiscussion={setShowPostDiscussion}
            />
          )}

          {activeZone === "messages" && (
            <MessagesZone
              user={user} conversations={conversations} activeConversation={activeConversation}
              conversationMessages={conversationMessages} newMessage={newMessage}
              setNewMessage={setNewMessage} fetchMessages={fetchMessages}
              handleSendMessage={handleSendMessage} setShowNewMessage={setShowNewMessage}
            />
          )}

          {activeZone === "lounge" && (
            <NetworkingLoungeZone
              setActiveZone={setActiveZone} setOpportunityFilter={setOpportunityFilter}
              fetchOpportunities={fetchOpportunities}
            />
          )}

          {activeZone === "marketplace" && (
            <MarketplaceZone
              opportunities={opportunities} opportunityFilter={opportunityFilter}
              setOpportunityFilter={setOpportunityFilter} fetchOpportunities={fetchOpportunities}
              expandedOppId={expandedOppId} setExpandedOppId={setExpandedOppId}
              setShowPostOpportunity={setShowPostOpportunity} openProfileCard={openProfileCard}
              setActiveZone={setActiveZone} formatDate={formatDate}
            />
          )}

          {activeZone === "pods" && (
            <MastermindPodsZone
              user={user} pods={pods} setSelectedPod={setSelectedPod}
              setShowEditPod={setShowEditPod} setShowCreatePod={setShowCreatePod}
              openPodDetails={openPodDetails} handleJoinPod={handleJoinPod}
              handleLeavePod={handleLeavePod} fetchWorkshopsAndPodcasts={fetchWorkshopsAndPodcasts}
            />
          )}

          {activeZone === "vault" && (
            <ResourceVaultZone
              user={user} resources={resources} resourceCategories={resourceCategories}
              resourceCategory={resourceCategory} setResourceCategory={setResourceCategory}
              fetchResources={fetchResources} expandedResourceId={expandedResourceId}
              setExpandedResourceId={setExpandedResourceId} setShowAddResource={setShowAddResource}
              handleEditResource={handleEditResource} handleDeleteResource={handleDeleteResource}
              setShowRequestRecommendation={setShowRequestRecommendation}
              fetchMyRecommendations={fetchMyRecommendations}
              fetchResourceCategories={fetchResourceCategories}
              API={API} authFetch={authFetch}
            />
          )}

          {activeZone === "podcast" && (
            <PodcastSuiteZone />
          )}

          {activeZone === "news" && (
            <NewsEventsZone
              user={user} newsFeed={newsFeed} events={events}
              expandedFeedId={expandedFeedId} setExpandedFeedId={setExpandedFeedId}
              setShowCreateBulletin={setShowCreateBulletin} setShowCreateEvent={setShowCreateEvent}
              handleRegisterEvent={handleRegisterEvent} stripHtml={stripHtml}
            />
          )}
        </div>
      </div>

      {/* All Dialogs */}
      <CommunityDialogs
        user={user}
        showPostOpportunity={showPostOpportunity} setShowPostOpportunity={setShowPostOpportunity}
        newOpportunity={newOpportunity} setNewOpportunity={setNewOpportunity} handlePostOpportunity={handlePostOpportunity}
        showEditProfile={showEditProfile} setShowEditProfile={setShowEditProfile}
        EditProfileForm={EditProfileForm} fetchInitialData={fetchInitialData}
        showIntroductionDialog={showIntroductionDialog} setShowIntroductionDialog={setShowIntroductionDialog}
        introductionText={introductionText} setIntroductionText={setIntroductionText} handlePostIntroduction={handlePostIntroduction}
        showCollaborationDialog={showCollaborationDialog} setShowCollaborationDialog={setShowCollaborationDialog}
        collaborationGoal={collaborationGoal} setCollaborationGoal={setCollaborationGoal} handleSetCollaborationGoal={handleSetCollaborationGoal}
        showPhotoUpload={showPhotoUpload} setShowPhotoUpload={setShowPhotoUpload} handlePhotoUpload={handlePhotoUpload}
        showUpgradeDialog={showUpgradeDialog} setShowUpgradeDialog={setShowUpgradeDialog}
        membershipStatus={membershipStatus} handleUpgrade={handleUpgrade}
        showCreateBulletin={showCreateBulletin} setShowCreateBulletin={setShowCreateBulletin}
        newBulletin={newBulletin} setNewBulletin={setNewBulletin} handleCreateBulletin={handleCreateBulletin}
        showCreateEvent={showCreateEvent} setShowCreateEvent={setShowCreateEvent}
        newEvent={newEvent} setNewEvent={setNewEvent} handleCreateEvent={handleCreateEvent}
        showPostDiscussion={showPostDiscussion} setShowPostDiscussion={setShowPostDiscussion}
        newDiscussionPost={newDiscussionPost} setNewDiscussionPost={setNewDiscussionPost} handleCreateDiscussionPost={handleCreateDiscussionPost}
        showComments={showComments} setShowComments={setShowComments}
        selectedPost={selectedPost} postComments={postComments}
        newComment={newComment} setNewComment={setNewComment}
        handleAddComment={handleAddComment} openProfileCard={openProfileCard}
        showNewMessage={showNewMessage} setShowNewMessage={setShowNewMessage}
        membersDirectory={membersDirectory} handleStartConversation={handleStartConversation}
        showAddResource={showAddResource} setShowAddResource={setShowAddResource}
        editingResource={editingResource} setEditingResource={setEditingResource}
        newResource={newResource} setNewResource={setNewResource}
        resourceCategories={resourceCategories}
        handleAddResource={handleAddResource} handleUpdateResource={handleUpdateResource}
        showRequestRecommendation={showRequestRecommendation} setShowRequestRecommendation={setShowRequestRecommendation}
        newRecommendation={newRecommendation} setNewRecommendation={setNewRecommendation}
        handleSubmitRecommendation={handleSubmitRecommendation} myRecommendations={myRecommendations}
        showPodDetails={showPodDetails} setShowPodDetails={setShowPodDetails}
        selectedPod={selectedPod} setSelectedPod={setSelectedPod}
        podMembers={podMembers} setShowAddPodContent={setShowAddPodContent}
        setShowEditPod={setShowEditPod} handleDeletePod={handleDeletePod}
        fetchWorkshopsAndPodcasts={fetchWorkshopsAndPodcasts} handleRemovePodContent={handleRemovePodContent}
        showCreatePod={showCreatePod} setShowCreatePod={setShowCreatePod}
        newPod={newPod} setNewPod={setNewPod} handleCreatePod={handleCreatePod}
        showEditPod={showEditPod} handleUpdatePod={handleUpdatePod}
        showAddPodContent={showAddPodContent}
        newPodContent={newPodContent} setNewPodContent={setNewPodContent} handleAddPodContent={handleAddPodContent}
        profileCardUserId={profileCardUserId} setProfileCardUserId={setProfileCardUserId}
        profileCardData={profileCardData} setProfileCardData={setProfileCardData}
        profileCardLoading={profileCardLoading} handleConnect={handleConnect}
      />
    </div>
  );
}
